// src/components/shared/GetAppButton.js
// Bannière "Obtenir l'application" - pleine largeur, sous le header, ne cache jamais rien

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import IOSInstallGuide from './IOSInstallGuide';
import AndroidInstallGuide from './AndroidInstallGuide';

const isStandalone = () => {
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
};

const detectPlatform = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'desktop';
};

const GetAppButton = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState('desktop');
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [showAndroidGuide, setShowAndroidGuide] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setVisible(false);
      return;
    }
    setPlatform(detectPlatform());
    setVisible(true);

    // Précharge les images du guide en arrière-plan, avant même le clic
    const iosImages = [
      '/onboarding/ios-step0.jpg',
      '/onboarding/ios-step1.jpg',
      '/onboarding/ios-step2.jpg',
      '/onboarding/ios-step3.jpg',
      '/onboarding/ios-step4.jpg',
    ];
    iosImages.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, []);

  const handleClick = () => {
    if (platform === 'ios') setShowIOSGuide(true);
    else if (platform === 'android') setShowAndroidGuide(true);
    else setShowAndroidGuide(true);
  };

  const styles = {
    wrapper: { padding: '0 16px', marginBottom: '10px' },
    button: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '11px 16px',
      backgroundColor: theme.gold,
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
      animation: 'getAppPulse 3s ease-in-out infinite',
      WebkitTapHighlightColor: 'transparent',
    },
    icon: { fontSize: '16px', lineHeight: 1 },
  };

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes getAppPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 3px 10px rgba(0,0,0,0.2); }
          50% { transform: scale(1.015); box-shadow: 0 5px 14px rgba(0,0,0,0.25); }
        }
      `}</style>
      <div style={styles.wrapper}>
        <button style={styles.button} onClick={handleClick}>
          <span style={styles.icon}>📲</span>
          {t('getApp.button')}
        </button>
      </div>

      <IOSInstallGuide open={showIOSGuide} onClose={() => setShowIOSGuide(false)} />
      <AndroidInstallGuide open={showAndroidGuide} onClose={() => setShowAndroidGuide(false)} />
    </>
  );
};

export default GetAppButton;